import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  EditPriceSearchIcon,
  DeletePriceSearchIcon,
  AddButton,
  WarningIcon,
  IconWarning,
  ModalSuccess,
  ModalClosed,
  ClearMM,
} from "~misc/icons";
import ModalAction from "~components/Modal/ModalAction";
import NewNegotiationModal from "./NewNegotiationModal";
import LostMappingModal from "./LostMappingModal";
import { db } from "~db/db";
import { formatDateByPattern, formatMonthYear, isValidBRDate, isValidMonthYear } from "~pages/FunnelV2/helpers/funnelHelpers";
import { isInvalidNegotiatedVolume } from "../../helpers/isInvalidNegotiatedVolume";
import { isPastNegotiationDate } from "../../helpers/pastDate";

const statusStyles = {
  default: "border-gray-300 bg-white text-primaria",
  edited: "border-[#034EA2] bg-[#E2F0FF] text-[#034EA2]",
};

const onlyDigits = (v) => (v || "").replace(/\D/g, "");

const Row = memo(({
  item,
  isSelected,
  onToggleSelect,
  statusOptions,
  onRowEdited,
  onRowReset,
  onRowInvalid,
  emNegociacaoStatusId,
  naoNegociadoStatusId,
  savedAt,
  onFunnelRefetch,
  onRequestPerdida
}) => {
  const [isEdited, setIsEdited] = useState(false);
  const [originalFunnel, setOriginalFunnel] = useState(null);
  const [volumeNegociado, setVolumeNegociado] = useState(item?.volumeNegociado);
  const [volumeMapeado,] = useState(item.volumeMapeado);
  const [negociacao, setDataNegociacao] = useState("");
  const [status, setStatus] = useState(item?.status);
  const isVolumeInvalid = isInvalidNegotiatedVolume(volumeNegociado, status, naoNegociadoStatusId);
  const today = new Date();

  const commit = (next) => {
    if (!originalFunnel) return;
    const originalDate = new Date(originalFunnel.dataNegociacao);
    originalDate.setHours(0, 0, 0, 0);

    const volumeChanged =
      (originalFunnel.volumeNegociado).toString() !== (next?.volumeNegociado ?? "").toString();
    const volumeMapeadoChanged = originalFunnel.volumeMapeado !== volumeMapeado;
    const statusChanged = String(originalFunnel.funilStatusId) !== String(next?.status);

    const formattedDate = formatDateByPattern(originalDate, "yyyy-mm");
    let dateChanged = formattedDate !== next?.dataNegociacao;

    const changed = volumeChanged || volumeMapeadoChanged || statusChanged || dateChanged;
    
    setIsEdited(changed);
    if (changed) {
        onRowEdited?.(item.id);
      } else {
        onRowReset?.(item.id);
      }
  };

  const updateDb = (data) => {
    return db.funilObraDashboard.update(item.id, data);
  }

  const handleVolume = async (value) => {
    const nextVolume = onlyDigits(value);
    const val = nextVolume.length ? nextVolume.replace(/^0+(?=\d)/, "") : "0";
    setVolumeNegociado(val);
    await updateDb({ volumeNegociado: parseInt(val) });
    onFunnelRefetch?.();
    commit({ volumeNegociado: val, dataNegociacao: negociacao, status });
  };

  const handleDataNegociacao = async (value) => {
    setDataNegociacao(value);
    if(isValidMonthYear(value?.toString()?.split('-')?.reverse()?.join('/'))) {
      await updateDb({ dataNegociacao: value + "-01"  });
      onFunnelRefetch?.();
    }
    commit({ volumeNegociado, dataNegociacao: value, status });
  };

  const handleStatus = async (value) => {
    const val = parseInt(value);

    const selected = statusOptions?.find(
      s => String(s.value) === String(val)
    );

    if (selected?.label?.toLowerCase() === "perdida") {
      onRequestPerdida?.( item.id, selected, (statusId) => {
          setStatus(statusId);
          commit({volumeNegociado, dataNegociacao: negociacao, status: statusId});
        }
      );
      return;
    }

    setStatus(val);
    await updateDb({ funilObraStatusId: val });
    onFunnelRefetch?.();
    commit({ volumeNegociado, dataNegociacao: negociacao, status: val });
  };

  const handleReset = async () => {
    const toUpdate = await getFunnelByItem();

    if(toUpdate) {
      const originalDateRaw = String(toUpdate.dataNegociacao).substring(0, 10);

      setVolumeNegociado(toUpdate.volumeNegociado);
      setDataNegociacao(formatMonthYear(formatDateByPattern(new Date(`${originalDateRaw}T00:00:00`), "yyyy-mm")));
      setStatus(toUpdate.funilStatusId);

      await updateDb({
        volumeNegociado: toUpdate.volumeNegociado,
        dataNegociacao: originalDateRaw,
        funilObraStatusId: toUpdate.funilStatusId,
      });
      onFunnelRefetch?.();
      setIsEdited(false);
      onRowReset?.(item.id);
    }
  };

  const getFunnelByItem = async () => {
    if(typeof item.id != 'string') {
      return await db.funilObra.get(item.id);
    }

    return null;
  }


  const isPastNegotiation = useMemo(() => {
    const value = negociacao?.split('-')?.reverse()?.join('/');
    const currentStatus = statusOptions?.find(s => String(s.value) === String(status));

    if (!isValidMonthYear(value)) return false;
    return isPastNegotiationDate(negociacao, currentStatus?.value, emNegociacaoStatusId);
  }, [negociacao, status, statusOptions, emNegociacaoStatusId]);

  useEffect(() => {
    onRowInvalid?.({id: item.id, invalid: isVolumeInvalid, isDateEmpty: !negociacao, isDate: true});
  }, [isVolumeInvalid, negociacao, item.id, onRowInvalid]);

  useEffect(() => {
    return () => onRowInvalid?.({id: item.id, invalid: false, isDate: false});
  }, [item.id]);

  const fieldStyle = (isEdited || isPastNegotiation) ? statusStyles.edited : statusStyles.default;
  const isEditedRef = useRef(isEdited);
  isEditedRef.current = isEdited;

  useEffect(() => {
    if (savedAt > 0) {
      setIsEdited(false);
    }
  }, [savedAt]);

  useEffect(() => {
    if (!isEditedRef.current) {
      setDataNegociacao(item.dataNegociacao);
      setVolumeNegociado(item.volumeNegociado);
      setStatus(item.status);
    }
  }, [item.dataNegociacao, item.volumeNegociado, item.status]);

  useEffect(() => {
    if(originalFunnel)
      commit({ volumeNegociado, dataNegociacao: negociacao, status })
  }, [originalFunnel])


  useEffect(() => {
    if(item.dataNegociacao)
      commit({ volumeNegociado, dataNegociacao: item.dataNegociacao, status });
  }, [item.dataNegociacao]);

  useEffect(() => {
    (async() => {
      const funnel = await getFunnelByItem();
      if(funnel) {
        setOriginalFunnel({
          ...funnel,
          dataNegociacaoFormatada: formatDateByPattern(new Date(`${funnel.dataNegociacao}T00:00:00`), "dd/mm/YYYY")
        });
      } else {
        if (typeof item.id == 'string') {
          setIsEdited(true);
        }
      }
    })();

    today.setHours(0, 0, 0, 0);
  }, []);

  return (
    <div className={`grid gap-4 items-center ${isPastNegotiation ? "grid-cols-[40px_120px_170px_20px_0.98fr_36px]" : "grid-cols-[40px_120px_170px_0.98fr_36px]"}`}>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onToggleSelect?.(item.id)}
        className="w-5 h-5 rounded border-gray-300 accent-primaria"
      />
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={volumeNegociado}
          onChange={(e) => handleVolume(e.target.value)}
          className={`h-10 w-full px-3 rounded-md border text-sm font-semibold focus:outline-none ${fieldStyle}`}
        />
        {isVolumeInvalid && (
          <p className="absolute -bottom-4 text-xs text-red-500" role="alert">
            Valor inválido
          </p>
        )}
      </div>
      <div className="relative">
        <input
          type="month"
          value={negociacao}
          onChange={(e) => handleDataNegociacao(e.target.value)}
          className={`h-10 w-full px-3 rounded-md border text-sm font-semibold focus:outline-none ${fieldStyle}`}
        />
        {!negociacao && (
          <p className="absolute -bottom-4 text-xs text-red-500" role="alert">
            Preencha a data de negociação.
          </p>
        )}
      </div>
      {isPastNegotiation && (
        <span className="flex items-center justify-center">
          <IconWarning className="w-5 h-5" fill="#FF2E00" />
        </span>
      )}
      <div className="relative">
        <select
          value={status}
          onChange={(e) => handleStatus(e.target.value)}
          className={`h-10 w-full pl-3 pr-8 rounded-md border text-sm font-semibold focus:outline-none appearance-none ${fieldStyle}`}
        >
          {
            (statusOptions ?? []).map(s => (
              <>
              <option value={s.value}>{s.label}</option>
              </>

            ))
          }
        </select>
        <svg
          className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 ${isEdited ? "text-[#034EA2]" : "text-primaria"}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </div>
      {(isEdited || isPastNegotiation) ? (
        <button
          type="button"
          onClick={handleReset}
          aria-label="Limpar edição"
          className="w-9 h-9 rounded-full bg-[#034EA2] flex items-center justify-center hover:opacity-90 transition"
        >
          <span className="scale-75">
            <ClearMM />
          </span>
        </button>
      ) : (
        <span />
      )}
    </div>
  );
});

Row.displayName = "TrackingGroupRow";

const TrackingGroupCard = ({
  id,
  statusOptions = [],
  title,
  volumeMapeado,
  unidade = "m³",
  proximaNegociacao,
  hasWarning = false,
  items = [],
  selectedIds = [],
  onToggleSelect,
  onEdit,
  onAdd,
  onDelete,
  onRowEdited,
  onRowReset,
  onRowInvalid,
  emNegociacaoStatusId,
  naoNegociadoStatusId,
  savedAt,
  onFunnelRefetch,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [modalPerdida, setModalPerdida] = useState(false);
  const [pendingPerdida, setPendingPerdida] = useState(null);

  const handleConfirmDelete = () => {
    onDelete?.();
    setConfirmOpen(false);
  };

  const handleSubmitPerdida = async (values) => {
    if (!pendingPerdida) return;

    const preco = (values.precoNecessario ?? "").replace("R$", "").trim().replace(/\./g, "").replace(",", ".");

    await db.funilObraDashboard.update(pendingPerdida.rowId, {
      ...values,
      funilObraStatusId: pendingPerdida.statusValue.value,
      precoNecessario: preco !== "" && preco !== null && preco !== 0 ? preco : null
    });

    pendingPerdida.onSuccess?.(
      pendingPerdida.statusValue.value
    );

    setModalPerdida(false);
    setPendingPerdida(null);
    onFunnelRefetch?.();
  };

  return (
    <div className="bg-[#E6E6EE] rounded-xl shadow mb-6 ">
      <div className="rounded-md overflow-hidden">
        <div className="bg-[#034EA2] text-white px-6 py-3 grid grid-cols-3 items-center">
          <span className="font-bold text-sm tracking-wide">{title}</span>
          <div className="flex items-center justify-center gap-2 text-sm">
            <span>
              Volume Mapeado: <strong>{volumeMapeado}{unidade}</strong>
            </span>
            {hasWarning && <WarningIcon className="w-4 h-4" />}
          </div>
          <span className="text-sm text-right">
            Próxima Negociação: <strong>{proximaNegociacao}</strong>
          </span>
        </div>
        <div className="bg-[#E6E6EE] ">
          <div className="bg-[#CFCFDF] px-6 py-3 mt-4 rounded-t-lg mx-4">
            <div className="flex items-center">
              <div className="grid grid-cols-[40px_120px_160px_1fr_36px] gap-4 items-center flex-1 text-xs font-bold text-primaria">
                <span />
                <span>Volume Negociado</span>
                <span className="leading-tight">Próx.<br />Negociação</span>
                <span>Status</span>
                <span />
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={onEdit} aria-label="Editar">
                  <EditPriceSearchIcon className="w-10 h-10" />
                </button>
                <button type="button" onClick={() => setConfirmOpen(true)} aria-label="Excluir">
                  <DeletePriceSearchIcon bgColor="#FF2E00" className="w-10 h-10" />
                </button>
              </div>
            </div>
          </div>

          <div className="bg-[#E6E6EE] px-6 py-4">
            <div className="flex flex-col gap-2">
              {items.map((item) => (
                <Row
                  statusOptions={statusOptions}
                  key={item.id}
                  item={item}
                  isSelected={selectedIds.includes(item.id)}
                  onToggleSelect={onToggleSelect}
                  onRowEdited={onRowEdited}
                  onRowReset={onRowReset}
                  onRowInvalid={onRowInvalid}
                  emNegociacaoStatusId={emNegociacaoStatusId}
                  naoNegociadoStatusId={naoNegociadoStatusId}
                  savedAt={savedAt}
                  onFunnelRefetch={onFunnelRefetch}
                  onRequestPerdida={(rowId, statusValue, onSuccess) => {
                    setPendingPerdida({ rowId, statusValue, onSuccess });
                    setModalPerdida(true);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <NewNegotiationModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        defaultGrupoProduto={title}
        onSubmit={(values) => {
          onAdd?.(values);
          setAddOpen(false);
        }}
      />

      <ModalAction
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        status="alert"
        title="Atenção"
        sub="Os volumes selecionados serão excluídos, deseja continuar?"
        buttonOne="VOLTAR"
        buttonTwo="EXCLUIR"
        linkOne={() => setConfirmOpen(false)}
        linkTwo={handleConfirmDelete}
      />

      <LostMappingModal
        isOpen={modalPerdida}
        onClose={() => {
          setModalPerdida(false);
          setPendingPerdida(null);
        }}
        defaultGrupoProduto={title}
        onSubmit={handleSubmitPerdida}
      />
    </div>
  );
};

const TrackingGroupCardMemo = memo(TrackingGroupCard);

export default TrackingGroupCardMemo;
